import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { Wallet, Mail, Lock, ArrowRight, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 order-2 lg:order-1">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md space-y-6 sm:space-y-8"
        >
          {/* Back Button - Mobile */}
          <button
            onClick={() => navigate('/')}
            className="lg:hidden flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-4"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Home</span>
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo-link">
            <Wallet className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-400" />
            <span className="text-xl sm:text-2xl font-outfit font-bold gradient-text">Dexfolio</span>
          </Link>

          {/* Header */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-white">Welcome back</h1>
            <p className="text-white/60 mt-2 text-sm sm:text-base">Sign in to access your trading portfolio</p>
          </div>

          {/* Error Alert */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-2 p-3 rounded-lg bg-hotpink-400/10 border border-hotpink-400/30 text-hotpink-400"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80 text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  className="input-field pl-10"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80 text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  className="input-field pl-10"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-white/60 text-sm sm:text-base">
            Don't have an account?{' '}
            <Link to="/register" className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium" data-testid="register-link">
              Create one
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-8 relative overflow-hidden order-1 lg:order-2">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGJsb2NrY2hhaW4lMjBub2Rlc3xlbnwwfHx8fDE3NzM0Nzk0MzR8MA&ixlib=rb-4.1.0&q=85)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#020408]" />
        
        <div className="relative z-10 text-center max-w-md px-8">
          <h2 className="text-3xl lg:text-4xl font-outfit font-bold text-white mb-4">
            Start Growing Your <span className="gradient-text">Wealth</span>
          </h2>
          <p className="text-white/60">
            Access your portfolio, track your trading activity, and watch your profits grow daily.
          </p>
        </div>
      </div>

      {/* Mobile Hero Section */}
      <div className="lg:hidden relative h-48 sm:h-56 overflow-hidden order-1">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1639322537228-f710d846310a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGJsb2NrY2hhaW4lMjBub2Rlc3xlbnwwfHx8fDE3NzM0Nzk0MzR8MA&ixlib=rb-4.1.0&q=85)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020408] via-transparent to-[#020408]" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <h2 className="text-xl sm:text-2xl font-outfit font-bold text-white mb-2">
              Start Growing Your <span className="gradient-text">Wealth</span>
            </h2>
            <p className="text-white/60 text-sm">Access your portfolio anytime, anywhere</p>
          </div>
        </div>
      </div>
    </div>
  );
}
